import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApi } from '../hooks/useApi';
import logger from "../utils/logger";
import { fmtDateTime } from '../utils/ruDates';
const DEFAULT_API_URL = 'https://api.vsegpt.ru/v1/chat/completions';
const CONTROL_API_URL = 'enilex-control://default';
const REF_LINK = 'https://vsegpt.ru/?cmpad=p66810047202';
const PAGE_SIZE = 50;
const BULK_BATCH_SIZE = 10;
const DEFAULT_PROMPT_SEARCH = 'Ты помощник по маркетплейсам. Собери в интернете полные данные и дай точное описание особенностей, характеристик и потребительских свойств товара (5-10 предложений), включая сильные стороны. Анализируй в том числе информацию из отзывов, карточек товара и вопросов покупателей. Узнай производителя товара (автора если книга). Не используй HTML и *. Не повторяй название и бренд.';
const DEFAULT_PROMPT_KEYWORDS = 'Сделай список ключевых запросов для товара. Не переспамливай и добавляй только максимально релевантные запросы. Учитывай название, бренд, материалы и свойства товара. Обязательно используй разные способы написания брендов, включая варианты на русском, английском языках и возможные опечатки, если не знаешь такие варианты - переводи принудительно наименование бренда с английского на русский или с русского на английский и используй оба варианта, например Kosadaka = Косадака. Учитывай разный порядок слов и словоформы, синонимы и т.п. Делай перечисления разных возможностей запросов покупателей. Просто список ключевых фраз через точку с запятой, без кавычек, без HTML, без нумерации и лишнего текста. Пример: ключ1; ключ2; ключ3.';
const DEFAULT_PROMPT_OZON_TITLE = 'Создай одно SEO-название для OZON по правилам: Бренд (если есть) + Тип товара + Название (если есть) + Важные характеристики. Супер строгое ограничение всего названия - 200 символов. Не ставь . в конце. Убери упоминания подарков.';
const DEFAULT_PROMPT_OZON_DESCRIPTION = 'Создай одно описание для OZON, используя принципы нейромаркетинга. Читабельное, понятное, без спама. Добавь ключевые фразы из списка с учетом их словоформы и порядка слов. Максимальная длина описания - 6000 символов, разрешены теги только: <br>, <ul>, <li>. СТРОЖАЙШЕ Запрещены теги заголовков H и теги утолщения <strong>, <b>, * или курсива, <em>, <p>. Не ставь точку в конце. Проверяй, чтобы описание не обрывалось, а последнее предложение было логично и закончено. Убери упоминания подарков.';
const STATUS_LABELS = {
  pending: '⏳ Ожидает',
  info_ready: '📋 Инфо готово',
  keys_ready: '🔑 Ключи готовы',
  generated: '✨ Сгенерировано',
  approved: '✓ Одобрено',
  rejected: '✗ Отклонено',
  sent: '✓ Отправлено',
  error: '⚠ Ошибка'
};

/* ---- CSV helpers ---- */

const CSV_HEADERS = {
  sku: 'SKU',
  offer_id: 'Offer ID',
  current_name: 'Текущее название',
  current_description: 'Текущее описание',
  product_info: 'Информация о товаре',
  keywords: 'Ключевые запросы',
  new_name: 'Новое название',
  new_description: 'Новое описание',
  status: 'Статус'
};
const CSV_FIELDS = Object.keys(CSV_HEADERS);
const CSV_RU_TO_FIELD = Object.fromEntries(Object.entries(CSV_HEADERS).map(([k, v]) => [v, k]));
function escapeCsvField(val) {
  const s = String(val || '').replace(/\r/g, '');
  if (s.includes(';') || s.includes('"') || s.includes('\n')) {
    return '"' + s.replace(/"/g, '""') + '"';
  }
  return s;
}
function toCsv(rows) {
  const headerLine = CSV_FIELDS.map(f => CSV_HEADERS[f]).join(';');
  const lines = [headerLine];
  rows.forEach(r => {
    lines.push(CSV_FIELDS.map(f => escapeCsvField(r[f])).join(';'));
  });
  return '\uFEFF' + lines.join('\n');
}
function parseCsvLine(line) {
  const fields = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') {
        current += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        current += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ';') {
        fields.push(current);
        current = '';
      } else {
        current += ch;
      }
    }
  }
  fields.push(current);
  return fields;
}
function parseCsv(text) {
  const rows = [];
  const lines = text.replace(/^\uFEFF/, '').split(/\r?\n/).filter(Boolean);
  if (!lines.length) return rows;
  const rawHeaders = parseCsvLine(lines[0]);
  const fieldMap = rawHeaders.map(h => {
    const trimmed = h.trim();
    return CSV_RU_TO_FIELD[trimmed] || trimmed;
  });
  for (let i = 1; i < lines.length; i++) {
    const parts = parseCsvLine(lines[i]);
    const row = {};
    fieldMap.forEach((key, idx) => {
      row[key] = parts[idx] || '';
    });
    rows.push(row);
  }
  return rows;
}

/* ---- Column definitions ---- */

const ALL_COLUMNS = [{
  key: 'current_name',
  label: 'Тек. название',
  editable: false,
  default: true
}, {
  key: 'current_description',
  label: 'Тек. описание',
  editable: false,
  default: true
}, {
  key: 'product_info',
  label: 'Информация',
  editable: true,
  default: true
}, {
  key: 'keywords',
  label: 'Ключи',
  editable: true,
  default: true
}, {
  key: 'new_name',
  label: 'Новое название',
  editable: true,
  default: true
}, {
  key: 'new_description',
  label: 'Новое описание',
  editable: true,
  default: true
}];
function OzonSeoAiPanel({
  accountId,
  accountName
}) {
  const {
    request,
    showStatus
  } = useApi();

  /* ---- Settings state ---- */
  const [settingsForm, setSettingsForm] = useState({
    api_url: DEFAULT_API_URL,
    model_search: 'perplexity/sonar-online',
    model_cheap: 'google/gemini-3-flash-pre-thinking',
    model_expensive: 'openai/gpt-5.2-chat',
    prompt_search: DEFAULT_PROMPT_SEARCH,
    prompt_keywords: DEFAULT_PROMPT_KEYWORDS,
    prompt_ozon_title: DEFAULT_PROMPT_OZON_TITLE,
    prompt_ozon_description: DEFAULT_PROMPT_OZON_DESCRIPTION
  });
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [apiKeySaved, setApiKeySaved] = useState(false);
  const [provider, setProvider] = useState('vsegpt');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [usageOpen, setUsageOpen] = useState(false);
  const [balanceData, setBalanceData] = useState(null);

  /* ---- Tab state ---- */
  const [activeTab, setActiveTab] = useState('products');

  /* ---- Products state ---- */
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState('');
  const [selected, setSelected] = useState({});
  const [genModel, setGenModel] = useState('cheap');
  const [visibleCols, setVisibleCols] = useState(() => {
    const init = {};
    ALL_COLUMNS.forEach(c => {
      init[c.key] = c.default;
    });
    return init;
  });

  /* ---- History state ---- */
  const [history, setHistory] = useState([]);
  const [historySelected, setHistorySelected] = useState({});
  const selectedIds = useMemo(() => Object.keys(selected).filter(k => selected[k]).map(k => Number(k)), [selected]);
  const hasAccount = Boolean(accountId);
  const debounceRef = useRef(null);

  /* ---- Debounced search ---- */
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedQuery(query), 400);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  /* ---- Model name for AI calls ---- */
  const getModelName = useCallback(() => {
    return genModel === 'expensive' ? settingsForm.model_expensive : settingsForm.model_cheap;
  }, [genModel, settingsForm.model_cheap, settingsForm.model_expensive]);

  /* ---- Load settings ---- */
  const loadSettings = async () => {
    try {
      const data = await request('/api/v1/ozon-seo-ai/settings');
      setApiKeySaved(Boolean(data.api_key_set));
      const next = {
        api_url: data.api_url || DEFAULT_API_URL,
        model_search: data.model_search || settingsForm.model_search,
        model_cheap: data.model_cheap || settingsForm.model_cheap,
        model_expensive: data.model_expensive || settingsForm.model_expensive,
        prompt_search: data.prompt_search || DEFAULT_PROMPT_SEARCH,
        prompt_keywords: data.prompt_keywords || DEFAULT_PROMPT_KEYWORDS,
        prompt_ozon_title: data.prompt_ozon_title || DEFAULT_PROMPT_OZON_TITLE,
        prompt_ozon_description: data.prompt_ozon_description || DEFAULT_PROMPT_OZON_DESCRIPTION
      };
      setSettingsForm(next);
      setProvider(next.api_url === CONTROL_API_URL ? 'control' : next.api_url === DEFAULT_API_URL ? 'vsegpt' : 'custom');
      if (data.api_key_set) setSettingsOpen(false);
    } catch (e) {
      showStatus('error', String(e.message || e));
    }
  };

  /* ---- Load products ---- */
  const loadProducts = async (pageArg = page) => {
    if (!hasAccount) return;
    setLoading(true);
    try {
      const offset = (pageArg - 1) * PAGE_SIZE;
      const qs = new URLSearchParams();
      if (statusFilter) qs.set('status', statusFilter);
      if (debouncedQuery) qs.set('q', debouncedQuery);
      qs.set('limit', String(PAGE_SIZE));
      qs.set('offset', String(offset));
      const data = await request(`/api/v1/ozon-seo-ai/${accountId}/products?${qs.toString()}`);
      setProducts(data.rows || []);
      setTotal(Number(data.total || 0));
    } catch (e) {
      showStatus('error', String(e.message || e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    loadSettings();
  }, []);
  useEffect(() => {
    setPage(1);
    loadProducts(1);
  }, [accountId, statusFilter, debouncedQuery]);
  useEffect(() => {
    loadProducts(page);
  }, [page]);

  /* ---- Settings actions ---- */
  const handleSaveSettings = async () => {
    try {
      const payload = {
        ...settingsForm
      };
      if (apiKeyInput) payload.api_key = apiKeyInput;
      const data = await request('/api/v1/ozon-seo-ai/settings', {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      setApiKeySaved(Boolean(data.api_key_set));
      setApiKeyInput('');
      showStatus('success', 'Настройки сохранены');
    } catch (e) {
      showStatus('error', String(e.message || e));
    }
  };
  const handleTestSettings = async () => {
    try {
      await request('/api/v1/ozon-seo-ai/settings/test', {
        method: 'POST',
        body: JSON.stringify({
          api_url: settingsForm.api_url,
          api_key: apiKeyInput || undefined,
          model: settingsForm.model_search
        })
      });
      showStatus('success', 'API работает');
    } catch (e) {
      showStatus('error', String(e.message || e));
    }
  };
  const loadBalance = async () => {
    if (provider !== 'vsegpt' || !apiKeySaved) {
      showStatus('error', 'Сначала сохраните API ключ VseGPT');
      return;
    }
    try {
      const response = await request('/api/v1/ozon-seo-ai/settings/vsegpt-balance', {
        method: 'GET'
      });
      logger.info('VseGPT Balance Response:', response);
      setBalanceData(response);
      showStatus('success', 'Баланс обновлен');
    } catch (e) {
      showStatus('error', String(e.message || e));
    }
  };

  /* ---- Product actions ---- */
  const handleImport = async () => {
    if (!hasAccount) return;
    setLoading(true);
    setActionMessage('Импорт товаров из атрибутов...');
    try {
      const data = await request(`/api/v1/ozon-seo-ai/${accountId}/products/import`, {
        method: 'POST',
        body: JSON.stringify({
          products: []
        })
      });
      showStatus('success', `Импортировано: ${data.imported || 0}`);
      loadProducts(1);
    } catch (e) {
      showStatus('error', String(e.message || e));
    } finally {
      setLoading(false);
      setActionMessage('');
    }
  };
  const updateLocal = (id, field, value) => {
    setProducts(prev => prev.map(p => p.id === id ? {
      ...p,
      [field]: value
    } : p));
  };
  const saveField = async (id, field, value) => {
    if (!hasAccount) return;
    try {
      await request(`/api/v1/ozon-seo-ai/${accountId}/products/${id}`, {
        method: 'PUT',
        body: JSON.stringify({
          [field]: value
        })
      });
    } catch (e) {
      showStatus('error', String(e.message || e));
    }
  };
  const runBulkAction = async ({
    path,
    label,
    modelName
  }) => {
    if (!hasAccount || selectedIds.length === 0) {
      showStatus('error', 'Выберите товары');
      return;
    }
    setLoading(true);
    const totalCount = selectedIds.length;
    let done = 0;
    setActionMessage(`${label}: 0/${totalCount}`);
    try {
      for (let i = 0; i < selectedIds.length; i += BULK_BATCH_SIZE) {
        const batch = selectedIds.slice(i, i + BULK_BATCH_SIZE);
        await request(`/api/v1/ozon-seo-ai/${accountId}/${path}`, {
          method: 'POST',
          body: JSON.stringify({
            product_ids: batch,
            model_name: modelName,
            marketplace: 'ozon'
          })
        });
        done += batch.length;
        setActionMessage(`${label}: ${done}/${totalCount}`);
      }
      showStatus('success', `${label}: готово (${totalCount})`);
      loadProducts(page);
    } catch (e) {
      showStatus('error', String(e.message || e));
    } finally {
      setLoading(false);
      setActionMessage('');
    }
  };
  const handleApprove = async id => {
    try {
      await request(`/api/v1/ozon-seo-ai/${accountId}/products/${id}/approve`, {
        method: 'POST'
      });
      loadProducts(page);
    } catch (e) {
      showStatus('error', String(e.message || e));
    }
  };
  const handleReject = async id => {
    try {
      await request(`/api/v1/ozon-seo-ai/${accountId}/products/${id}/reject`, {
        method: 'POST'
      });
      loadProducts(page);
    } catch (e) {
      showStatus('error', String(e.message || e));
    }
  };
  const handleBulkApprove = async () => {
    if (!hasAccount || selectedIds.length === 0) {
      showStatus('error', 'Выберите товары');
      return;
    }
    setLoading(true);
    setActionMessage(`Одобрение ${selectedIds.length} товаров...`);
    try {
      for (const id of selectedIds) {
        await request(`/api/v1/ozon-seo-ai/${accountId}/products/${id}/approve`, {
          method: 'POST'
        });
      }
      showStatus('success', `Одобрено: ${selectedIds.length}`);
      setSelected({});
      loadProducts(page);
    } catch (e) {
      showStatus('error', String(e.message || e));
    } finally {
      setLoading(false);
      setActionMessage('');
    }
  };
  const handleBulkReject = async () => {
    if (!hasAccount || selectedIds.length === 0) {
      showStatus('error', 'Выберите товары');
      return;
    }
    setLoading(true);
    setActionMessage(`Отклонение ${selectedIds.length} товаров...`);
    try {
      for (const id of selectedIds) {
        await request(`/api/v1/ozon-seo-ai/${accountId}/products/${id}/reject`, {
          method: 'POST'
        });
      }
      showStatus('success', `Отклонено: ${selectedIds.length}`);
      setSelected({});
      loadProducts(page);
    } catch (e) {
      showStatus('error', String(e.message || e));
    } finally {
      setLoading(false);
      setActionMessage('');
    }
  };

  /* ---- CSV ---- */
  const handleDownload = () => {
    const csv = toCsv(products);
    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'seo-ai-товары.csv';
    a.click();
    URL.revokeObjectURL(url);
  };
  const handleDownloadTemplate = () => {
    const csv = '\uFEFF' + CSV_FIELDS.map(f => CSV_HEADERS[f]).join(';') + '\n';
    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'seo-ai-шаблон.csv';
    a.click();
    URL.revokeObjectURL(url);
  };
  const handleUpload = async file => {
    if (!file || !hasAccount) return;
    const text = await file.text();
    const rows = parseCsv(text);
    if (!rows.length) {
      showStatus('error', 'CSV пуст или неверный формат');
      return;
    }
    try {
      const payload = {
        products: rows.map(r => ({
          sku: r.sku || '',
          offer_id: r.offer_id || null,
          name: r.current_name || null,
          description: r.current_description || null,
          product_info: r.product_info || null,
          product_info_source: r.product_info ? 'manual' : 'empty',
          keywords: r.keywords || null,
          keywords_source: r.keywords ? 'manual' : 'empty',
          new_name: r.new_name || null,
          new_description: r.new_description || null,
          status: r.status || null
        }))
      };
      const data = await request(`/api/v1/ozon-seo-ai/${accountId}/products/import`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      showStatus('success', `: ${data.imported || 0}`);
      loadProducts(1);
    } catch (e) {
      showStatus('error', String(e.message || e));
    }
  };

  /* ---- Send to OZON ---- */
  const handleSendToOzon = async () => {
    if (!hasAccount || selectedIds.length === 0) {
      showStatus('error', 'Выберите товары');
      return;
    }
    if (!window.confirm(`Отправить ${selectedIds.length} товаров в OZON?`)) return;
    setLoading(true);
    try {
      const data = await request(`/api/v1/ozon-seo-ai/${accountId}/send-to-ozon`, {
        method: 'POST',
        body: JSON.stringify({
          product_ids: selectedIds
        })
      });
      showStatus('success', `Отправлено: ${data.items_sent || 0}, пропущено: ${data.items_skipped || 0}`);
      loadProducts(page);
      setSelected({});
    } catch (e) {
      showStatus('error', String(e.message || e));
    } finally {
      setLoading(false);
    }
  };

  /* ---- History ---- */
  const loadHistory = async () => {
    if (!hasAccount) return;
    try {
      const data = await request(`/api/v1/ozon-seo-ai/${accountId}/history?limit=100&offset=0`);
      setHistory(data.rows || []);
    } catch (e) {
      showStatus('error', String(e.message || e));
    }
  };
  const handleRevert = async () => {
    const historyIds = Object.keys(historySelected).filter(k => historySelected[k]).map(k => Number(k));
    if (historyIds.length === 0) {
      showStatus('error', 'Выберите записи для отката');
      return;
    }
    if (!window.confirm(`Откатить ${historyIds.length} изменений?`)) return;
    try {
      const data = await request(`/api/v1/ozon-seo-ai/${accountId}/revert`, {
        method: 'POST',
        body: JSON.stringify({
          history_ids: historyIds
        })
      });
      showStatus('success', `Откатлено: ${data.items_reverted || 0}`);
      loadHistory();
      setHistorySelected({});
    } catch (e) {
      showStatus('error', String(e.message || e));
    }
  };
  useEffect(() => {
    if (activeTab === 'history') {
      loadHistory();
    }
  }, [activeTab]);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const activeColumns = ALL_COLUMNS.filter(c => visibleCols[c.key]);

  /* ---- Render: no account ---- */
  if (!hasAccount) {
    return <div className="seo-ai-panel">
                <div className="seo-ai-panel__empty">
                    Подключите кабинет OZON в разделе &laquo;Настройки&raquo;, чтобы увидеть данные.
                </div>
            </div>;
  }
  return <div className="seo-ai-panel">
            {/* ===== Collapsible settings ===== */}
            <div className="seo-ai-settings">
                <button type="button" className="seo-ai-settings__toggle" onClick={() => setSettingsOpen(v => !v)}>
          
                    <span>Настройки AI</span>
                    <span className="seo-ai-settings__arrow">{settingsOpen ? '\u25B2' : '\u25BC'}</span>
                    {(apiKeySaved || provider === 'control') && !settingsOpen && <span className="seo-ai-settings__badge">API подключен</span>}
                </button>

                {settingsOpen && <div className="seo-ai-settings__body">
                        <div className="seo-ai-settings__row">
                            <label>
                                Провайдер
                                <select value={provider} onChange={e => {
              const next = e.target.value;
              setProvider(next);
              if (next === 'vsegpt' || next === 'control') {
                setSettingsForm(prev => ({
                  ...prev,
                  api_url: next === 'control' ? CONTROL_API_URL : DEFAULT_API_URL
                }));
              }
            }}>
                
                                    <option value="vsegpt">VSE GPT</option>
                                    <option value="control">Энилекс.Контроль</option>
                                    <option value="custom">Свой провайдер</option>
                                </select>
                            </label>
                            <div className="seo-ai-settings__ref">
                                Рекомендуем <a href={REF_LINK} target="_blank" rel="noreferrer">VSEGPT</a>
                            </div>
                        </div>

                        {provider === 'control' && <div className="seo-ai-settings__hint">
                                Генерация идёт через ИИ платформы: ключ и модель не нужны, поиска в интернете нет. Оплата по фактическим токенам прямо с баланса.
                            </div>}

                        {provider !== 'control' && <div className="seo-ai-settings__row">
                            <label>
                                API URL
                                <input type="text" value={settingsForm.api_url} onChange={e => setSettingsForm(prev => ({
              ...prev,
              api_url: e.target.value
            }))} disabled={provider === 'vsegpt'} autoComplete="off" spellCheck={false} />
              
                            </label>
                            <label>
                                API key
                                <input type="password" name="ozon-seo-ai-api-key" value={apiKeyInput} placeholder={apiKeySaved ? '••••••••' : 'Введите ключ'} onChange={e => setApiKeyInput(e.target.value)} autoComplete="new-password" data-lpignore="true" spellCheck={false} />
              
                                {apiKeySaved && <span className="seo-ai-settings__hint">
                                        Ключ сохранен. Чтобы заменить — введите новый и нажмите «Сохранить».
                                    </span>}
                            </label>
                        </div>}

                        {provider !== 'control' && <div className="seo-ai-settings__row">
                            <label>
                                Модель поиска
                                <input type="text" value={settingsForm.model_search} onChange={e => setSettingsForm(prev => ({
              ...prev,
              model_search: e.target.value
            }))} />
              
                            </label>
                            <label>
                                Модель 1
                                <input type="text" value={settingsForm.model_cheap} onChange={e => setSettingsForm(prev => ({
              ...prev,
              model_cheap: e.target.value
            }))} />
              
                            </label>
                            <label>
                                Модель 2
                                <input type="text" value={settingsForm.model_expensive} onChange={e => setSettingsForm(prev => ({
              ...prev,
              model_expensive: e.target.value
            }))} />
              
                            </label>
                        </div>}

                        <div className="seo-ai-settings__row seo-ai-settings__row--prompts">
                            <label>
                                Промпт поиска
                                <textarea rows={6} value={settingsForm.prompt_search} onChange={e => setSettingsForm(prev => ({
              ...prev,
              prompt_search: e.target.value
            }))} />
                            </label>
                            <label>
                                Промпт ключей
                                <textarea rows={6} value={settingsForm.prompt_keywords} onChange={e => setSettingsForm(prev => ({
              ...prev,
              prompt_keywords: e.target.value
            }))} />
                            </label>
                            <label>
                                Промпт названия OZON
                                <textarea rows={6} value={settingsForm.prompt_ozon_title} onChange={e => setSettingsForm(prev => ({
              ...prev,
              prompt_ozon_title: e.target.value
            }))} />
                            </label>
                            <label>
                                Промпт описания OZON
                                <textarea rows={6} value={settingsForm.prompt_ozon_description} onChange={e => setSettingsForm(prev => ({
              ...prev,
              prompt_ozon_description: e.target.value
            }))} />
                            </label>
                        </div>

                        <div className="seo-ai-settings__row">
                            <button type="button" className="primary" onClick={handleSaveSettings}>Сохранить</button>
                            {provider !== 'control' && <button type="button" className="ghost" onClick={handleTestSettings}>Проверить API</button>}
                        </div>

                        {/* ===== VseGPT Usage Block ===== */}
                        {provider === 'vsegpt' && <div className="seo-ai-usage">
                                <button type="button" className="seo-ai-usage__toggle" onClick={() => setUsageOpen(v => !v)}>
              
                                    <span>💰 Контроль расходов VseGPT</span>
                                    <span className="seo-ai-usage__arrow">{usageOpen ? '▲' : '▼'}</span>
                                </button>

                                {usageOpen && <div className="seo-ai-usage__body">
                                        <div className="seo-ai-usage__hint">
                                            <strong>Важно:</strong> Для работы функции проверки баланса нужно включить её в настройках VseGPT:
                                            <a href="https://vsegpt.ru/user/api" target="_blank" rel="noreferrer" style={{
                marginLeft: '4px',
                color: '#2196f3'
              }}>
                                                https://vsegpt.ru/user/api
                                            </a>
                                        </div>

                                        <div className="seo-ai-usage__actions">
                                            <button type="button" className="ghost" onClick={loadBalance}>
                                                Обновить баланс
                                            </button>
                                        </div>

                                        {balanceData ? <div className="seo-ai-usage__data">
                                                {balanceData.warning && <div className="seo-ai-usage__warning">
                                                        {balanceData.warning}
                                                    </div>}
                                                <div className="seo-ai-usage__item">
                                                    <span className="seo-ai-usage__label">Баланс (кредиты):</span>
                                                    <span className="seo-ai-usage__value">
                                                        {balanceData.balance !== undefined && balanceData.balance !== null ? balanceData.balance.toFixed(2) : 'N/A'}
                                                    </span>
                                                </div>
                                                {balanceData.subscription_status && <div className="seo-ai-usage__item">
                                                        <span className="seo-ai-usage__label">Статус подписки:</span>
                                                        <span className="seo-ai-usage__value">
                                                            {balanceData.subscription_status === 'ok' ? 'Активна' : balanceData.subscription_status}
                                                        </span>
                                                    </div>}
                                                {balanceData.subscription_end && <div className="seo-ai-usage__item">
                                                        <span className="seo-ai-usage__label">Подписка до:</span>
                                                        <span className="seo-ai-usage__value">
                                                            {fmtDateTime(balanceData.subscription_end)}
                                                        </span>
                                                    </div>}
                                                {balanceData.last_updated && <div className="seo-ai-usage__item">
                                                        <span className="seo-ai-usage__label">Обновлено:</span>
                                                        <span className="seo-ai-usage__value">
                                                            {fmtDateTime(balanceData.last_updated)}
                                                        </span>
                                                    </div>}
                                                {balanceData.raw_response && <div className="seo-ai-usage__debug">
                                                        <details>
                                                            <summary style={{
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    color: '#999'
                  }}>
                                                                Отладка (raw response)
                                                            </summary>
                                                            <pre style={{
                    fontSize: '0.7rem',
                    overflow: 'auto',
                    maxHeight: '200px',
                    background: '#f5f5f5',
                    padding: '8px',
                    borderRadius: '4px',
                    marginTop: '8px'
                  }}>
                                                                {JSON.stringify(balanceData.raw_response, null, 2)}
                                                            </pre>
                                                        </details>
                                                    </div>}
                                            </div> : <div className="seo-ai-usage__empty">
                                                Нажмите «Обновить баланс», чтобы загрузить данные.
                                            </div>}
                                    </div>}
                            </div>}
                    </div>}
            </div>

            {/* ===== AI Generation area ===== */}
            <div className="seo-ai-gen">
                <div className="seo-ai-gen__group">
                    <span className="seo-ai-gen__label">AI генерация:</span>
                    <button type="button" className="ghost" onClick={() => runBulkAction({
          path: 'search-info',
          label: 'Поиск информации',
          modelName: settingsForm.model_search
        })} disabled={loading || selectedIds.length === 0}>
            
                        Поиск информации
                    </button>
                    <button type="button" className="ghost" onClick={() => runBulkAction({
          path: 'generate-keywords',
          label: 'Ключевые запросы',
          modelName: settingsForm.model_cheap
        })} disabled={loading || selectedIds.length === 0}>
            
                        Ключевые запросы
                    </button>
                    <button type="button" className="ghost" onClick={() => runBulkAction({
          path: 'generate-content',
          label: 'Генерация контента',
          modelName: getModelName()
        })} disabled={loading || selectedIds.length === 0}>
            
                        Сгенерировать контент
                    </button>
                    <label className="seo-ai-gen__model">
                        <span>Модель:</span>
                        <select value={genModel} onChange={e => setGenModel(e.target.value)}>
                            <option value="cheap">{settingsForm.model_cheap}</option>
                            <option value="expensive">{settingsForm.model_expensive}</option>
                        </select>
                    </label>
                    <span className="seo-ai-gen__note">
                        Поиск: {settingsForm.model_search}. Ключи: {settingsForm.model_cheap}. Генерация: {getModelName()}.
                        Контекст: инфо о товаре, ключи, текущее название/описание и атрибуты.
                    </span>
                </div>
                <div className="seo-ai-gen__actions">
                    <button type="button" className="ghost" onClick={handleBulkApprove} disabled={loading || selectedIds.length === 0} title="Одобрить выбранные товары">
            
                        ✓ Одобрить выбранные
                    </button>
                    <button type="button" className="ghost" onClick={handleBulkReject} disabled={loading || selectedIds.length === 0} title="Отклонить выбранные товары">
            
                        ✗ Отклонить выбранные
                    </button>
                </div>
                <div className="seo-ai-gen__send">
                    <button type="button" className="seo-ai-send-btn primary" onClick={handleSendToOzon} disabled={loading || selectedIds.length === 0}>
            
                        Отправить в OZON
                    </button>
                </div>
            </div>

            {/* ===== Tabs ===== */}
            <div className="seo-ai-tabs">
                <button type="button" className={activeTab === 'products' ? 'seo-ai-tabs__btn active' : 'seo-ai-tabs__btn'} onClick={() => setActiveTab('products')}>
          
                    Товары
                </button>
                <button type="button" className={activeTab === 'history' ? 'seo-ai-tabs__btn active' : 'seo-ai-tabs__btn'} onClick={() => setActiveTab('history')}>
          
                    История
                </button>
            </div>

            {activeTab === 'products' && <>
            {/* ===== Filters + table toolbar ===== */}
            <div className="seo-ai-toolbar">
                {actionMessage && <div className="seo-ai-toolbar__status">{actionMessage}</div>}
                <div className="seo-ai-toolbar__left">
                    <label>
                        Статус
                        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                            <option value="">Все</option>
                            {Object.keys(STATUS_LABELS).map(k => <option key={k} value={k}>{STATUS_LABELS[k]}</option>)}
                        </select>
                    </label>
                    <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Поиск по артикулу / Offer ID / названию" className="seo-ai-toolbar__search" />
            
                    <span className="seo-ai-toolbar__total">Всего: {total}</span>
                </div>
                <div className="seo-ai-toolbar__right">
                    <button type="button" className="ghost" onClick={handleImport}>Импортировать товары из атрибутов</button>
                    <button type="button" className="ghost" onClick={handleDownload}>Скачать CSV</button>
                    <button type="button" className="ghost" onClick={handleDownloadTemplate}>Скачать шаблон CSV</button>
                    <label className="ghost seo-ai-toolbar__upload">
                        Загрузить CSV
                        <input type="file" accept=".csv" onChange={e => {
              handleUpload(e.target.files?.[0]);
              e.target.value = '';
            }} />
                    </label>
                </div>
            </div>

            {/* ===== Column toggles ===== */}
            <div className="seo-ai-columns">
                <span>Столбцы:</span>
                {ALL_COLUMNS.map(c => <label key={c.key} className="seo-ai-columns__item">
                        <input type="checkbox" checked={visibleCols[c.key]} onChange={e => setVisibleCols(prev => ({
            ...prev,
            [c.key]: e.target.checked
          }))} />
            
                        <span>{c.label}</span>
                    </label>)}
            </div>

            {/* ===== Table ===== */}
            <div className="seo-ai-table">
                <table>
                    <thead>
                        <tr>
                            <th className="seo-ai-table__check">
                                <input type="checkbox" checked={selectedIds.length > 0 && selectedIds.length === products.length} onChange={e => {
                  const next = {};
                  products.forEach(p => {
                    next[p.id] = e.target.checked;
                  });
                  setSelected(next);
                }} />
                  
                            </th>
                            <th className="seo-ai-table__product">Товар</th>
                            {activeColumns.map(c => <th key={c.key}>{c.label}</th>)}
                            <th className="seo-ai-table__status">Статус</th>
                            <th className="seo-ai-table__actions">Действия</th>
                        </tr>
                    </thead>
                    <tbody>
                        {products.map(p => <tr key={p.id} className={p.status === 'error' ? 'seo-ai-table__row--error' : ''}>
                                <td className="seo-ai-table__check">
                                    <input type="checkbox" checked={Boolean(selected[p.id])} onChange={e => setSelected(prev => ({
                  ...prev,
                  [p.id]: e.target.checked
                }))} />
                  
                                </td>
                                <td className="seo-ai-table__product">
                                    <div className="seo-ai-table__sku-info">
                                        <strong>{p.sku}</strong>
                                        {p.offer_id && <span>{p.offer_id}</span>}
                                        {p.brand && <span>{p.brand}</span>}
                                    </div>
                                </td>
                                {activeColumns.map(c => <td key={c.key}>
                                        {c.editable ? <textarea value={p[c.key] || ''} onChange={e => updateLocal(p.id, c.key, e.target.value)} onBlur={e => saveField(p.id, c.key, e.target.value)} rows={3} /> : <span>{p[c.key] || ''}</span>}
                                    </td>)}
                                <td className="seo-ai-table__status">
                                    <span className={`seo-ai-table__badge seo-ai-table__badge--${p.status}`}>
                                        {STATUS_LABELS[p.status] || p.status}
                                    </span>
                                    {p.error_message && <span className="seo-ai-table__error" title={p.error_message}>!</span>}
                                </td>
                                <td className="seo-ai-table__actions">
                                    <button type="button" className="ghost seo-ai-table__btn" onClick={() => handleApprove(p.id)} title="Одобрить">
                                        &#10003;
                                    </button>
                                    <button type="button" className="ghost seo-ai-table__btn seo-ai-table__btn--reject" onClick={() => handleReject(p.id)} title="Отклонить">
                                        &#10007;
                                    </button>
                                </td>
                            </tr>)}
                    </tbody>
                </table>
                {loading && <div className="seo-ai-table__loading">Загрузка...</div>}
                {!loading && products.length === 0 && <div className="seo-ai-table__empty">Нет товаров. Нажмите &laquo;Импортировать товары из атрибутов&raquo; для импорта.</div>}
            </div>

            {/* ===== Pagination ===== */}
            <div className="seo-ai-pager">
                <button type="button" className="ghost" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>Назад</button>
                <span>{page} / {totalPages}</span>
                <button type="button" className="ghost" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>Вперед</button>
                <button type="button" className="ghost" onClick={() => loadProducts(page)}>Обновить</button>
            </div>
                </>}

            {activeTab === 'history' && <div className="seo-ai-history">
                    <div className="seo-ai-history__toolbar">
                        <button type="button" className="primary" onClick={handleRevert} disabled={Object.values(historySelected).filter(Boolean).length === 0}>
            
                            Откатить выбранные
                        </button>
                    </div>

                    <div className="seo-ai-table">
                        <table>
                            <thead>
                                <tr>
                                    <th className="seo-ai-table__check">
                                        <input type="checkbox" checked={history.length > 0 && Object.keys(historySelected).filter(k => historySelected[k]).length === history.length} onChange={e => {
                  const next = {};
                  history.forEach(h => {
                    next[h.id] = e.target.checked;
                  });
                  setHistorySelected(next);
                }} />
                  
                                    </th>
                                    <th>SKU</th>
                                    <th>Offer ID</th>
                                    <th>Поле</th>
                                    <th>Старое значение</th>
                                    <th>Новое значение</th>
                                    <th>Статус</th>
                                    <th>Откат</th>
                                    <th>Дата</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map(h => <tr key={h.id} className={h.rolled_back ? 'seo-ai-history__row--rolled' : ''}>
                                        <td className="seo-ai-table__check">
                                            <input type="checkbox" checked={Boolean(historySelected[h.id])} onChange={e => setHistorySelected(prev => ({
                  ...prev,
                  [h.id]: e.target.checked
                }))} disabled={h.rolled_back} />
                  
                                        </td>
                                        <td>{h.sku}</td>
                                        <td>{h.offer_id || ''}</td>
                                        <td>{h.field}</td>
                                        <td className="seo-ai-history__value">{h.old_value || ''}</td>
                                        <td className="seo-ai-history__value">{h.new_value || ''}</td>
                                        <td>
                                            <span className={h.success ? 'seo-ai-history__success' : 'seo-ai-history__error'}>
                                                {h.success ? 'Успешно' : 'Ошибка'}
                                            </span>
                                        </td>
                                        <td>{h.rolled_back ? 'Откат выполнен' : ''}</td>
                                        <td>{h.created_at ? fmtDateTime(h.created_at) : ''}</td>
                                    </tr>)}
                            </tbody>
                        </table>
                        {history.length === 0 && <div className="seo-ai-table__empty">История пуста.</div>}
                    </div>
                </div>}
        </div>;
}
export default OzonSeoAiPanel;
